import useCrmObjectName from '../hooks/useCrmObjectName';
/**
 * Inline component for usage in JSX lang keys (translation keys ending with `_jsx`) to support
 * translations of object type names into languages with different capitalization rules.
 *
 * This component is designed to be used as a placeholder in JSX lang keys (translation keys ending with `_jsx`).
 * It allows translators to move the object type name within a sentence and apply language-specific capitalization rules.
 *
 * Example usage in a translation file (en.lyaml):
 *
 *   someKey_jsx: "Search all <CrmObjectTypeName lowercase={true} plural={true} />."
 *
 * Example usage in React:
 *
 *   <FormattedJSXMessage
 *     message="marketingPlatformLib.someKey_jsx"
 *     elements={{ CrmObjectTypeName: (props) => <CrmObjectTypeName objectTypeId="0-35" {...props} /> }}
 *   />
 *
 * Props:
 *   - objectTypeId: string (required) — The CRM object type ID.
 *   - typeDefinition: CrmObjectTypeDefinition (optional) — Optionally provide a type definition for custom names.
 *   - skip: boolean (optional) — Skip fetching the type definition.
 *   - plural: boolean (optional) — If true, renders the plural form.
 *   - capitalize: boolean (optional) — If true, applies capitalization to the name.
 */
export function CrmObjectTypeName({
  objectTypeId,
  typeDefinition,
  skip,
  plural,
  lowercase
}) {
  const {
    objectTypeNamePlural,
    objectTypeNameSingular
  } = useCrmObjectName(objectTypeId, {
    textTransform: lowercase ? 'lowercase' : 'capitalize',
    typeDefinition,
    skip
  });
  return plural ? objectTypeNamePlural : objectTypeNameSingular;
}